import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { BurndownPoint } from '../../services/jiraData';
interface SprintBurndownProps {
  data?: BurndownPoint[];
  isLoading?: boolean;
}
export function SprintBurndown({
  data,
  isLoading
}: SprintBurndownProps) {
  if (isLoading) {
    return <Card className="h-full bg-slate-900 border-slate-800" data-id="element-2685">
        <CardHeader className="pb-2" data-id="element-2686">
          <div className="flex items-center justify-between animate-pulse" data-id="element-2687">
            <div className="h-5 bg-slate-800 rounded w-32" data-id="element-2688"></div>
            <div className="h-5 bg-slate-800 rounded-full w-24" data-id="element-2689"></div>
          </div>
        </CardHeader>
        <CardContent data-id="element-2690">
          <div className="h-32 w-full bg-slate-800/50 rounded-lg animate-pulse mb-4" data-id="element-2691"></div>
          <div className="flex justify-between animate-pulse" data-id="element-2692">
            <div className="h-3 bg-slate-800 rounded w-16" data-id="element-2693"></div>
            <div className="h-3 bg-slate-800 rounded w-16" data-id="element-2694"></div>
          </div>
        </CardContent>
      </Card>;
  }
  // If no real data, show the mock SVG burndown
  if (!data || data.length === 0) {
    return <MockBurndown data-id="element-2695" />;
  }
  // ─── Real Burndown ────────────────────────────────────────────────────
  const maxPoints = Math.max(...data.map(d => d.ideal), ...data.filter(d => d.actual !== null).map(d => d.actual!));
  const chartH = 120;
  const chartW = 100; // percentage-based
  const padTop = 8;
  const padBottom = 4;
  const scaleY = (val: number) => {
    return padTop + (maxPoints - val) / maxPoints * (chartH - padTop - padBottom);
  };
  const scaleX = (idx: number) => {
    return idx / (data.length - 1) * chartW;
  };
  // Build SVG paths
  const idealPoints = data.map((d, i) => `${scaleX(i)},${scaleY(d.ideal)}`).join(' ');
  const actualData = data.filter(d => d.actual !== null);
  const actualPoints = actualData.map((d, i) => {
    const idx = data.indexOf(d);
    return `${scaleX(idx)},${scaleY(d.actual!)}`;
  }).join(' ');
  // Area under actual line
  const lastActualIdx = data.indexOf(actualData[actualData.length - 1]);
  const actualArea = actualPoints + ` ${scaleX(lastActualIdx)},${chartH - padBottom} ${scaleX(0)},${chartH - padBottom}`;
  // Today marker
  const todayIdx = actualData.length - 1;
  const todayX = scaleX(data.indexOf(actualData[todayIdx]));
  const todayY = scaleY(actualData[todayIdx].actual!);
  // Pick ~5 labels evenly spaced
  const labelCount = Math.min(5, data.length);
  const labelStep = Math.max(1, Math.floor((data.length - 1) / (labelCount - 1)));
  const labelIndices: number[] = [];
  for (let i = 0; i < data.length; i += labelStep) labelIndices.push(i);
  if (labelIndices[labelIndices.length - 1] !== data.length - 1) labelIndices.push(data.length - 1);
  // Current remaining
  const currentRemaining = actualData.length > 0 ? actualData[actualData.length - 1].actual : null;
  const idealNow = actualData.length > 0 ? data[data.indexOf(actualData[actualData.length - 1])].ideal : null;
  const isAhead = currentRemaining !== null && idealNow !== null && currentRemaining <= idealNow;
  return <Card className="h-full bg-slate-900 border-slate-800" data-id="element-2696">
      <CardHeader className="pb-2" data-id="element-2697">
        <div className="flex items-center justify-between" data-id="element-2698">
          <CardTitle className="text-sm font-medium text-slate-400" data-id="element-2699">
            Burndown Chart
          </CardTitle>
          {currentRemaining !== null && <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${isAhead ? 'bg-emerald-950/50 text-emerald-400 border border-emerald-900/50' : 'bg-amber-950/50 text-amber-400 border border-amber-900/50'}`} data-id="element-2700">
              {currentRemaining} pts restantes
            </span>}
        </div>
      </CardHeader>
      <CardContent data-id="element-2701">
        <div className="h-32 w-full relative" data-id="element-2702">
          <svg className="w-full h-full" viewBox={`0 0 ${chartW} ${chartH}`} preserveAspectRatio="none" data-id="element-2703">
            <defs data-id="element-2704">
              <linearGradient id="burnGrad" x1="0" y1="0" x2="0" y2="1" data-id="element-2705">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" data-id="element-2706" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0" data-id="element-2707" />
              </linearGradient>
            </defs>

            {/* Ideal line (dashed) */}
            <polyline points={idealPoints} fill="none" stroke="#475569" strokeWidth="1.5" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" data-id="element-2708" />

            {/* Actual area fill */}
            {actualData.length > 1 && <polygon points={actualArea} fill="url(#burnGrad)" data-id="element-2709" />}

            {/* Actual line */}
            {actualData.length > 1 && <polyline points={actualPoints} fill="none" stroke="#6366f1" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-2710" />}

            {/* Today dot */}
            {actualData.length > 0 && <>
                <circle cx={todayX} cy={todayY} r="3" fill="#6366f1" stroke="#1e1b4b" strokeWidth="1.5" vectorEffect="non-scaling-stroke" data-id="element-2711" />

                <circle cx={todayX} cy={todayY} r="6" fill="#6366f1" opacity="0.2" vectorEffect="non-scaling-stroke" data-id="element-2712" />
              </>}
          </svg>
        </div>

        {/* X-axis labels */}
        <div className="flex justify-between mt-1 px-0.5" data-id="element-2713">
          {labelIndices.map(idx => <span key={idx} className="text-[10px] text-slate-600" data-id="element-2714">
              {data[idx].label}
            </span>)}
        </div>

        {/* Legend */}
        <div className="mt-3 flex justify-between text-xs" data-id="element-2715">
          <div className="flex items-center gap-2" data-id="element-2716">
            <span className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-2717"></span>
            <span className="text-slate-400" data-id="element-2718">Actual</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2719">
            <span className="w-2 h-2 rounded-full bg-slate-600" data-id="element-2720"></span>
            <span className="text-slate-400" data-id="element-2721">Ideal</span>
          </div>
        </div>
      </CardContent>
    </Card>;
}
/** Original mock burndown (static SVG) */
function MockBurndown() {
  return <Card className="h-full bg-slate-900 border-slate-800" data-id="element-2722">
      <CardHeader className="pb-2" data-id="element-2723">
        <CardTitle className="text-sm font-medium text-slate-400" data-id="element-2724">
          Burndown Chart
        </CardTitle>
      </CardHeader>
      <CardContent data-id="element-2725">
        <div className="h-32 w-full relative flex items-end justify-between px-2" data-id="element-2726">
          <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" data-id="element-2727">
            <line x1="0" y1="0" x2="100%" y2="100%" stroke="#475569" strokeWidth="2" strokeDasharray="4 4" data-id="element-2728" />

            <polyline points="0,0 20,10 40,15 60,40 80,45 100,60" fill="none" stroke="#6366f1" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-2729" />

            <polygon points="0,0 20,10 40,15 60,40 80,45 100,60 100,100 0,100" fill="url(#mockGradient)" opacity="0.2" data-id="element-2730" />

            <defs data-id="element-2731">
              <linearGradient id="mockGradient" x1="0" y1="0" x2="0" y2="1" data-id="element-2732">
                <stop offset="0%" stopColor="#6366f1" data-id="element-2733" />
                <stop offset="100%" stopColor="#6366f1" stopOpacity="0" data-id="element-2734" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute -bottom-6 w-full flex justify-between text-xs text-slate-500" data-id="element-2735">
            <span data-id="element-2736">Day 1</span>
            <span data-id="element-2737">Day 5</span>
            <span data-id="element-2738">Day 10</span>
          </div>
        </div>
        <div className="mt-8 flex justify-between text-xs" data-id="element-2739">
          <div className="flex items-center gap-2" data-id="element-2740">
            <span className="w-2 h-2 rounded-full bg-indigo-500" data-id="element-2741"></span>
            <span className="text-slate-400" data-id="element-2742">Actual</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-2743">
            <span className="w-2 h-2 rounded-full bg-slate-600" data-id="element-2744"></span>
            <span className="text-slate-400" data-id="element-2745">Ideal</span>
          </div>
        </div>
      </CardContent>
    </Card>;
}